import { useEffect, useMemo, useState } from 'react';
import { buildPrintData } from '../data/printData.js';
import type { PrintData, PrintSection } from '../data/printData.js';
import { todayIso } from '../data/util.js';
import { FrDateInput } from '../../ui/FrDateInput.js';
import { TEMPLATES } from './templates.js';
import type { MenuType, TemplateData, TemplateKey, TemplateOptions } from './templates.js';

/* ---- Font pairings + paper textures (ported from the WP printer) ---- */
const FONT_PAIRINGS = {
  serif: { display: "'Cormorant Garamond', 'EB Garamond', Georgia, serif", body: "'Cormorant Garamond', 'EB Garamond', Georgia, serif", ui: "'Inter', system-ui, sans-serif", label: 'Serif classique' },
  sans: { display: "'Inter', system-ui, sans-serif", body: "'Inter', system-ui, sans-serif", ui: "'Inter', system-ui, sans-serif", label: 'Sans moderne' },
  display: { display: "'Playfair Display', 'Cormorant Garamond', serif", body: "'Inter', system-ui, sans-serif", ui: "'Inter', system-ui, sans-serif", label: 'Display éditorial' },
  mixed: { display: "'Libre Caslon Display', 'Cormorant Garamond', serif", body: "'Cormorant Garamond', serif", ui: "'Work Sans', 'Inter', sans-serif", label: 'Caslon × Work Sans' },
} as const;
type FontKey = keyof typeof FONT_PAIRINGS;

const PAPER_TEXTURES = {
  cream: { bg: '#fbf7ee', label: 'Crème' },
  ivory: { bg: '#f7f1e4', label: 'Ivoire' },
  white: { bg: '#fdfcf9', label: 'Blanc' },
  kraft: { bg: '#e9dcc2', label: 'Kraft' },
  mist: { bg: '#eff1ec', label: 'Brume' },
} as const;
type TextureKey = keyof typeof PAPER_TEXTURES;

interface Cfg {
  menuType: MenuType;
  template: TemplateKey;
  paperSize: 'a4' | 'a5';
  orientation: 'portrait' | 'landscape';
  showPrices: boolean;
  showDate: boolean;
  showAllergenNote: boolean;
  showLogo: boolean;
  chefNote: string;
  accent: string;
  fontPairing: FontKey;
  paperTexture: TextureKey;
  carteCourseFilter: string[] | null;
  headlinePrice: string;
  extraPrice: string;
  dishScale: number;
  descScale: number;
  courseScale: number;
  metaScale: number;
  dishColor: string;
  descColor: string;
  courseColor: string;
  metaColor: string;
  selectedDate: string;
}
const DEFAULTS: Cfg = {
  menuType: 'daily',
  template: 'manuscrit',
  paperSize: 'a4',
  orientation: 'portrait',
  showPrices: true,
  showDate: false,
  showAllergenNote: true,
  showLogo: true,
  chefNote: 'Bon Appétit !',
  accent: '#6b5a2a',
  fontPairing: 'serif',
  paperTexture: 'cream',
  carteCourseFilter: null,
  headlinePrice: '20€',
  extraPrice: '+4€',
  dishScale: 2.6,
  descScale: 2.35,
  courseScale: 1.8,
  metaScale: 2.0,
  dishColor: '#2a3957',
  descColor: '#7a3b2e',
  courseColor: '#5b1f4a',
  metaColor: '#1c1814',
  selectedDate: todayIso(),
};

/* Per-menu-type defaults applied when the user switches type. Menu du Jour has
   1 dish/course (big type); Formule can have up to 3/course (smaller); Carte is
   dense (base size). Chef note defaults to empty except on the Menu du Jour. */
const TYPE_DEFAULTS: Record<MenuType, Pick<Cfg, 'dishScale' | 'descScale' | 'courseScale' | 'metaScale' | 'chefNote'>> = {
  daily: { dishScale: 2.6, descScale: 2.35, courseScale: 1.8, metaScale: 2.0, chefNote: 'Bon Appétit !' },
  formule: { dishScale: 1.55, descScale: 1.7, courseScale: 1.25, metaScale: 2.0, chefNote: '' },
  carte: { dishScale: 1.0, descScale: 1.0, courseScale: 1.0, metaScale: 1.0, chefNote: '' },
};

function formatFRDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

const TPL_LABELS: Record<TemplateKey, string> = { classique: 'Classique', moderne: 'Moderne', manuscrit: 'Manuscrit' };
const TYPE_LABELS: Record<MenuType, string> = { daily: 'Menu du Jour', formule: 'Formule', carte: 'Carte' };

export function Printer() {
  const [data, setData] = useState<PrintData | null>(null);
  const [err, setErr] = useState('');
  const [cfg, setCfgState] = useState<Cfg>(DEFAULTS);
  const [zoom, setZoom] = useState(0.62);
  const setCfg = (p: Partial<Cfg>) => setCfgState((prev) => ({ ...prev, ...p }));
  const selectType = (t: MenuType) => setCfgState((prev) => ({ ...prev, menuType: t, ...TYPE_DEFAULTS[t] }));

  const load = () => {
    setData(null);
    setErr('');
    buildPrintData()
      .then(setData)
      .catch((e) => setErr((e as Error).message));
  };
  useEffect(load, []);

  /* Inject live theme on .mp + concrete colour rules that survive PDF export. */
  useEffect(() => {
    let el = document.getElementById('mp-live-theme');
    if (!el) {
      el = document.createElement('style');
      el.id = 'mp-live-theme';
      document.head.appendChild(el);
    }
    const fp = FONT_PAIRINGS[cfg.fontPairing];
    const pt = PAPER_TEXTURES[cfg.paperTexture];
    const vars = [
      `--accent:${cfg.accent};`,
      `--font-display:${fp.display};`,
      `--font-body:${fp.body};`,
      `--font-ui:${fp.ui};`,
      `--paper-bg:${pt.bg};`,
      `--dish-scale:${cfg.dishScale};`,
      `--desc-scale:${cfg.descScale};`,
      `--course-scale:${cfg.courseScale};`,
      `--meta-scale:${cfg.metaScale};`,
      cfg.dishColor ? `--dish-color:${cfg.dishColor};` : '',
      cfg.descColor ? `--desc-color:${cfg.descColor};` : '',
      cfg.courseColor ? `--course-color:${cfg.courseColor};` : '',
      cfg.metaColor ? `--meta-color:${cfg.metaColor};` : '',
    ].filter(Boolean).join('');
    const printRules = [
      cfg.dishColor ? `.tpl-c-item-name,.tpl-m-item-name,.tpl-ms-item-name{color:${cfg.dishColor} !important}` : '',
      cfg.descColor ? `.tpl-c-item-desc,.tpl-m-item-desc,.tpl-ms-item-desc{color:${cfg.descColor} !important}` : '',
      cfg.courseColor ? `.tpl-c-section-title,.tpl-m-section-title,.tpl-ms-section-title{color:${cfg.courseColor} !important}` : '',
      cfg.metaColor ? `.tpl-c-date,.tpl-c-subtitle,.tpl-m-date,.tpl-ms-date,.tpl-ms-subtitle{color:${cfg.metaColor} !important}` : '',
      `.tpl-c-ornament,.tpl-c-item-price,.tpl-m-kicker,.tpl-m-slash,.tpl-m-num,.tpl-ms-flourish,.tpl-ms-num,.tpl-course-divider{color:${cfg.accent} !important}`,
      `.tpl-c-dotrow{background:${cfg.accent} !important}`,
    ].filter(Boolean).join('');
    el.textContent = `.mp{${vars}}\n${printRules}`;
    return () => { el?.remove(); };
  }, [cfg.accent, cfg.fontPairing, cfg.paperTexture, cfg.dishScale, cfg.descScale, cfg.courseScale, cfg.metaScale, cfg.dishColor, cfg.descColor, cfg.courseColor, cfg.metaColor]);

  /* Build the template's data for the active menu type. */
  const tplData: TemplateData | null = useMemo(() => {
    if (!data) return null;
    if (cfg.menuType === 'daily') {
      const day = data.DAILY.byDate[cfg.selectedDate] ?? { sections: [], courses: [] };
      return { subtitle: data.DAILY.subtitle, date: formatFRDate(cfg.selectedDate), sections: day.sections };
    }
    if (cfg.menuType === 'formule') {
      return { subtitle: data.FORMULE.subtitle, sections: data.FORMULE.sections };
    }
    const f = cfg.carteCourseFilter;
    const sections: PrintSection[] = f == null ? data.CARTE.sections : data.CARTE.sections.filter((s) => f.includes(s.courseSlug));
    return { subtitle: data.CARTE.subtitle, sections };
  }, [data, cfg.menuType, cfg.selectedDate, cfg.carteCourseFilter]);

  const carteCourses = data?.CARTE.courses ?? [];
  const options: TemplateOptions = {
    showPrices: cfg.showPrices,
    showDate: cfg.showDate,
    chefNote: cfg.chefNote,
    showAllergenNote: cfg.showAllergenNote,
    showLogo: cfg.showLogo,
    headlinePrice: cfg.headlinePrice,
    extraPrice: cfg.extraPrice,
  };
  const TemplateComp = TEMPLATES[cfg.template];
  const isCentered = cfg.menuType === 'daily' || cfg.menuType === 'formule';

  const toggleCourse = (slug: string) => {
    const all = carteCourses.map((c) => c.slug);
    const sel = cfg.carteCourseFilter;
    if (sel == null) { setCfg({ carteCourseFilter: all.filter((s) => s !== slug) }); return; }
    if (sel.includes(slug)) setCfg({ carteCourseFilter: sel.filter((s) => s !== slug) });
    else { const next = [...sel, slug]; setCfg({ carteCourseFilter: next.length === all.length ? null : next }); }
  };

  return (
    <div className="mp">
      <aside className="mp-sidebar app-noprint">
        <div className="mp-grp mp-actions">
          <button className="mp-btn mp-btn-primary" onClick={() => window.print()} disabled={!data}>Exporter en PDF</button>
          <button className="mp-btn mp-btn-ghost" onClick={() => setCfgState({ ...DEFAULTS, selectedDate: todayIso() })}>Réinitialiser</button>
          {err && <p className="mp-err">{err}</p>}
        </div>

        <div className="mp-grp">
          <div className="mp-grp-label">Note du Chef</div>
          <textarea value={cfg.chefNote} placeholder="Une citation, une dédicace…" onChange={(e) => setCfg({ chefNote: e.target.value })} />
        </div>

        <div className="mp-grp">
          <div className="mp-grp-label">Type de menu</div>
          <div className="mp-chips">
            {(['daily', 'formule', 'carte'] as MenuType[]).map((t) => (
              <button key={t} className="mp-chip" aria-pressed={cfg.menuType === t} onClick={() => selectType(t)}>{TYPE_LABELS[t]}</button>
            ))}
          </div>
          {cfg.menuType === 'daily' && (
            <>
              <label className="mp-field" style={{ marginTop: 10 }}>
                <span>Date</span>
                <FrDateInput value={cfg.selectedDate} onChange={(v) => setCfg({ selectedDate: v || todayIso() })} />
              </label>
              <label className="mp-toggle" style={{ marginTop: 8 }}>
                <input type="checkbox" checked={cfg.showDate} onChange={(e) => setCfg({ showDate: e.target.checked })} /> Imprimer la date
              </label>
            </>
          )}
        </div>

        {cfg.menuType === 'carte' && carteCourses.length > 0 && (
          <div className="mp-grp">
            <div className="mp-grp-label">Cours à imprimer</div>
            {carteCourses.map((c) => (
              <label key={c.slug} className="mp-toggle">
                <input type="checkbox" checked={cfg.carteCourseFilter == null || cfg.carteCourseFilter.includes(c.slug)} onChange={() => toggleCourse(c.slug)} />
                {c.label} <span style={{ color: 'var(--ui-muted)' }}>({c.count})</span>
              </label>
            ))}
          </div>
        )}

        {isCentered && (
          <div className="mp-grp">
            <div className="mp-grp-label">Tarifs</div>
            <label className="mp-field"><span>Prix d'entête (titre)</span><input type="text" value={cfg.headlinePrice} placeholder="20€" onChange={(e) => setCfg({ headlinePrice: e.target.value })} /></label>
            <label className="mp-field"><span>Supplément 3 plats</span><input type="text" value={cfg.extraPrice} placeholder="+4€" onChange={(e) => setCfg({ extraPrice: e.target.value })} /></label>
          </div>
        )}

        <div className="mp-grp">
          <div className="mp-grp-label">Modèle</div>
          <div className="mp-chips">
            {(Object.keys(TEMPLATES) as TemplateKey[]).map((k) => (
              <button key={k} className="mp-chip" aria-pressed={cfg.template === k} onClick={() => setCfg({ template: k })}>{TPL_LABELS[k]}</button>
            ))}
          </div>
        </div>

        <div className="mp-grp">
          <div className="mp-grp-label">Typographie</div>
          <div className="mp-chips mp-chips-2">
            {(Object.keys(FONT_PAIRINGS) as FontKey[]).map((k) => (
              <button key={k} className="mp-chip" aria-pressed={cfg.fontPairing === k} onClick={() => setCfg({ fontPairing: k })} style={{ fontFamily: FONT_PAIRINGS[k].display }}>{FONT_PAIRINGS[k].label}</button>
            ))}
          </div>
        </div>

        <div className="mp-grp">
          <div className="mp-grp-label">Papier</div>
          <div className="mp-chips mp-chips-5">
            {(Object.keys(PAPER_TEXTURES) as TextureKey[]).map((k) => (
              <button key={k} className="mp-chip" aria-pressed={cfg.paperTexture === k} onClick={() => setCfg({ paperTexture: k })} title={PAPER_TEXTURES[k].label}>
                <span className="mp-swatch" style={{ background: PAPER_TEXTURES[k].bg }} />
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <label className="mp-field" style={{ flex: 1 }}><span>Format</span>
              <select value={cfg.paperSize} onChange={(e) => setCfg({ paperSize: e.target.value as 'a4' | 'a5' })}><option value="a4">A4</option><option value="a5">A5</option></select>
            </label>
            <label className="mp-field" style={{ flex: 1 }}><span>Sens</span>
              <select value={cfg.orientation} onChange={(e) => setCfg({ orientation: e.target.value as 'portrait' | 'landscape' })}><option value="portrait">Portrait</option><option value="landscape">Paysage</option></select>
            </label>
          </div>
        </div>

        <div className="mp-grp">
          <div className="mp-grp-label">Lisibilité</div>
          <Scale label="Plats" value={cfg.dishScale} onChange={(v) => setCfg({ dishScale: v })} />
          <Scale label="Descriptions" value={cfg.descScale} onChange={(v) => setCfg({ descScale: v })} />
          <Scale label="Intitulés" value={cfg.courseScale} onChange={(v) => setCfg({ courseScale: v })} />
          <Scale label="En-tête" value={cfg.metaScale} onChange={(v) => setCfg({ metaScale: v })} />
        </div>

        <div className="mp-grp">
          <div className="mp-grp-label">Couleurs</div>
          <Color label="Plats" value={cfg.dishColor} onChange={(v) => setCfg({ dishColor: v })} />
          <Color label="Descriptions" value={cfg.descColor} onChange={(v) => setCfg({ descColor: v })} />
          <Color label="Intitulés" value={cfg.courseColor} onChange={(v) => setCfg({ courseColor: v })} />
          <Color label="En-tête" value={cfg.metaColor} onChange={(v) => setCfg({ metaColor: v })} />
          <Color label="Accent (ornements)" value={cfg.accent} onChange={(v) => setCfg({ accent: v })} />
        </div>

        <div className="mp-grp">
          <div className="mp-grp-label">Options</div>
          <label className="mp-toggle"><input type="checkbox" checked={cfg.showLogo} onChange={(e) => setCfg({ showLogo: e.target.checked })} /> Logo Le Montaigu</label>
          {cfg.menuType === 'carte' && (
            <label className="mp-toggle"><input type="checkbox" checked={cfg.showPrices} onChange={(e) => setCfg({ showPrices: e.target.checked })} /> Afficher les prix</label>
          )}
          <label className="mp-toggle"><input type="checkbox" checked={cfg.showAllergenNote} onChange={(e) => setCfg({ showAllergenNote: e.target.checked })} /> Mention allergènes</label>
        </div>
      </aside>

      <main className="mp-preview">
        <div className="mp-toolbar app-noprint">
          <div><strong>{TPL_LABELS[cfg.template]}</strong> · {TYPE_LABELS[cfg.menuType]} · {cfg.paperSize.toUpperCase()} {cfg.orientation === 'portrait' ? 'portrait' : 'paysage'}</div>
          <div className="mp-zoom">
            <button onClick={() => setZoom((z) => Math.max(0.2, +(z - 0.1).toFixed(2)))} aria-label="Zoom -">−</button>
            <span className="mp-zoom-val">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(1.5, +(z + 0.1).toFixed(2)))} aria-label="Zoom +">+</button>
            <button className="mp-btn mp-btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => setZoom(0.62)}>Fit</button>
          </div>
        </div>
        <div className="mp-stage">
          {!data && !err && <p className="mp-loading">Chargement…</p>}
          {data && tplData && (
            <div className="mp-paper-wrap" style={{ transform: `scale(${zoom})` }}>
              <div className="mp-paper" data-size={cfg.paperSize} data-orient={cfg.orientation} data-texture={cfg.paperTexture}>
                {tplData.sections.length === 0 ? (
                  <p className="mp-empty">Aucun plat pour cette sélection.</p>
                ) : (
                  <TemplateComp type={cfg.menuType} data={tplData} restaurant={data.RESTAURANT} options={options} />
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Scale({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="mp-scale">
      <span>{label} — {value.toFixed(2)}×</span>
      <input type="range" min="0.8" max="3" step="0.05" value={value} onChange={(e) => onChange(parseFloat(e.target.value))} />
    </label>
  );
}

function Color({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="mp-color">
      <span>{label}</span>
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
